
import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Save, ArrowLeft, Plus, Trash } from 'lucide-react';
import { 
  fetchLessonById, 
  createLesson, 
  updateLesson
} from '@/services/modules/lessonService';
import { 
  fetchLessonResources,
  createLessonResource,
  updateLessonResource,
  deleteLessonResource 
} from '@/services/modules/resourceService';
import { ModuleLesson, LessonResource, LessonType, TaxonomyLevel } from '@/types/supabase';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formSchema = z.object({
  title: z.string().min(3, { message: 'Title must be at least 3 characters long' }),
  description: z.string().optional(),
  content: z.string().optional(),
  taxonomy_level: z.string(),
  order_position: z.coerce.number().positive(),
  duration_minutes: z.coerce.number().optional(),
  is_completed: z.boolean().optional(),
  lesson_type: z.string(),
});

const resourceFormSchema = z.object({
  title: z.string().min(2, { message: 'Title is required' }),
  url: z.string().url({ message: 'Must be a valid URL' }),
  type: z.string(),
  is_required: z.boolean().optional(),
});

interface LessonEditorProps {
  moduleId: string;
  lessonId?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const LessonEditor: React.FC<LessonEditorProps> = ({ moduleId, lessonId, onCancel, onSuccess }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resources, setResources] = React.useState<LessonResource[]>([]);
  const [isAddingResource, setIsAddingResource] = React.useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      content: '',
      taxonomy_level: 'remember',
      order_position: 1,
      duration_minutes: 30,
      is_completed: false,
      lesson_type: 'theoretical_framework',
    },
  });

  const resourceForm = useForm<z.infer<typeof resourceFormSchema>>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues: {
      title: '',
      url: '',
      type: 'reading',
      is_required: false,
    },
  });

  const { data: currentLesson, isLoading: isLoadingLesson } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => fetchLessonById(lessonId!),
    enabled: !!lessonId,
  });

  const { data: lessonResources, isLoading: isLoadingResources } = useQuery({
    queryKey: ['lessonResources', lessonId],
    queryFn: () => fetchLessonResources(lessonId!),
    enabled: !!lessonId,
  });

  useEffect(() => {
    if (currentLesson) {
      form.reset({
        title: currentLesson.title,
        description: currentLesson.description || '',
        content: currentLesson.content || '',
        taxonomy_level: currentLesson.taxonomy_level,
        order_position: currentLesson.order_position,
        duration_minutes: currentLesson.duration_minutes || 30,
        is_completed: currentLesson.is_completed || false,
        lesson_type: currentLesson.lesson_type || 'theoretical_framework',
      });
    }
  }, [currentLesson, form]);

  useEffect(() => {
    if (lessonResources) {
      // Convert the raw data to the correct type
      const typedResources = lessonResources.map(resource => ({
        ...resource,
        type: resource.type as LessonResource['type']
      }));
      setResources(typedResources);
    }
  }, [lessonResources]);

  const createLessonMutation = useMutation({
    mutationFn: (data: Omit<ModuleLesson, 'id' | 'created_at' | 'updated_at'>) => 
      createLesson(data),
    onSuccess: (newLesson) => {
      if (newLesson) {
        queryClient.invalidateQueries({ queryKey: ['lessons', moduleId] });
        toast({
          title: 'Lesson created',
          description: `"${newLesson.title}" has been created successfully`,
        });
        onSuccess();
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to create lesson',
        description: 'There was an error creating the lesson. Please try again.',
        variant: 'destructive',
      });
      console.error('Error creating lesson:', error);
    },
  });

  const updateLessonMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<ModuleLesson> }) => 
      updateLesson(id, data),
    onSuccess: (updatedLesson) => {
      if (updatedLesson) {
        queryClient.invalidateQueries({ queryKey: ['lessons', moduleId] });
        queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] });
        toast({
          title: 'Lesson updated',
          description: `"${updatedLesson.title}" has been updated successfully`,
        });
        onSuccess();
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to update lesson',
        description: 'There was an error updating the lesson. Please try again.',
        variant: 'destructive',
      });
      console.error('Error updating lesson:', error);
    },
  });

  const createResourceMutation = useMutation({
    mutationFn: (data: Omit<LessonResource, 'id'>) => 
      createLessonResource({ ...data, lesson_id: lessonId || '' }),
    onSuccess: (newResource) => {
      if (newResource) {
        queryClient.invalidateQueries({ queryKey: ['lessonResources', lessonId] });
        toast({
          title: 'Resource added',
          description: `"${newResource.title}" has been added successfully`,
        });
        setResources(prev => [...prev, newResource as LessonResource]);
        resourceForm.reset();
        setIsAddingResource(false);
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to add resource',
        description: 'There was an error adding the resource. Please try again.',
        variant: 'destructive',
      });
      console.error('Error adding resource:', error);
    },
  });

  const deleteResourceMutation = useMutation({
    mutationFn: (resourceId: string) => deleteLessonResource(resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessonResources', lessonId] });
      toast({
        title: 'Resource deleted',
        description: 'The resource has been deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete resource',
        description: 'There was an error deleting the resource. Please try again.',
        variant: 'destructive',
      });
      console.error('Error deleting resource:', error);
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const lessonData = {
      ...values,
      module_id: moduleId,
      taxonomy_level: values.taxonomy_level as TaxonomyLevel,
      lesson_type: values.lesson_type as LessonType
    };

    if (lessonId) {
      updateLessonMutation.mutate({
        id: lessonId,
        data: lessonData,
      });
    } else {
      createLessonMutation.mutate(lessonData as Omit<ModuleLesson, 'id' | 'created_at' | 'updated_at'>);
    }
  };

  const onSubmitResource = (values: z.infer<typeof resourceFormSchema>) => {
    if (!lessonId) {
      toast({
        title: 'Save lesson first',
        description: 'You need to save the lesson before adding resources',
        variant: 'destructive',
      });
      return;
    }

    createResourceMutation.mutate({
      ...values,
      lesson_id: lessonId,
      type: values.type as LessonResource['type']
    } as any);
  };

  const handleDeleteResource = (resourceId: string) => {
    if (confirm('Are you sure you want to delete this resource?')) {
      deleteResourceMutation.mutate(resourceId);
      setResources(prev => prev.filter(resource => resource.id !== resourceId));
    }
  };

  const isLoading = isLoadingLesson || isLoadingResources;
  const isPending = createLessonMutation.isPending || updateLessonMutation.isPending;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onCancel} className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Lessons
        </Button>
        <h2 className="text-lg font-medium">{lessonId ? 'Edit Lesson' : 'Create New Lesson'}</h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lesson Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter lesson title" {...field} />
                    </FormControl>
                    <FormDescription>The name of this lesson</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lesson_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lesson Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select lesson type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="theoretical_framework">Theoretical Framework</SelectItem>
                        <SelectItem value="practical_application">Practical Application</SelectItem>
                        <SelectItem value="technical_component">Technical Component</SelectItem>
                        <SelectItem value="knowledge_acquisition">Knowledge Acquisition</SelectItem>
                        <SelectItem value="comprehension">Comprehension</SelectItem>
                        <SelectItem value="application">Application</SelectItem>
                        <SelectItem value="analysis">Analysis</SelectItem>
                        <SelectItem value="evaluation">Evaluation</SelectItem>
                        <SelectItem value="creation">Creation</SelectItem>
                        <SelectItem value="second_me_training">Second Me Training</SelectItem>
                        <SelectItem value="assessment">Assessment</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>The type of learning this lesson provides</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxonomy_level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taxonomy Level</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select taxonomy level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="remember">Remember</SelectItem>
                        <SelectItem value="understand">Understand</SelectItem>
                        <SelectItem value="apply">Apply</SelectItem>
                        <SelectItem value="analyze">Analyze</SelectItem>
                        <SelectItem value="evaluate">Evaluate</SelectItem>
                        <SelectItem value="create">Create</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>The cognitive level this lesson targets</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="30" {...field} />
                    </FormControl>
                    <FormDescription>Estimated time to complete this lesson</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="order_position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order Position</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="1" {...field} />
                    </FormControl>
                    <FormDescription>The position of this lesson in the module</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter lesson description"
                      className="min-h-[80px] resize-y"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>A brief description of what this lesson covers</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lesson Content</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter lesson content"
                      className="min-h-[200px] resize-y"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>The main content of the lesson (markdown supported)</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isPending}
              >
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {lessonId ? 'Update Lesson' : 'Create Lesson'}
              </Button>
            </div>
          </form>
        </Form>
      )}

      {lessonId && (
        <div className="space-y-4 pt-6 border-t">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Lesson Resources</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsAddingResource(!isAddingResource)}
            >
              {isAddingResource ? (
                'Cancel'
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </>
              )}
            </Button>
          </div>

          {isAddingResource && (
            <Card className="p-4">
              <Form {...resourceForm}>
                <form onSubmit={resourceForm.handleSubmit(onSubmitResource)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={resourceForm.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Resource Title</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter resource title" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={resourceForm.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Resource Type</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select resource type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="reading">Reading</SelectItem>
                              <SelectItem value="video">Video</SelectItem>
                              <SelectItem value="tool">Tool</SelectItem>
                              <SelectItem value="software">Software</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={resourceForm.control}
                      name="url"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>Resource URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      type="submit"
                      disabled={createResourceMutation.isPending}
                    >
                      {createResourceMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add Resource
                    </Button>
                  </div>
                </form>
              </Form>
            </Card>
          )}

          {resources.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resources.map((resource) => (
                  <TableRow key={resource.id}>
                    <TableCell className="font-medium">{resource.title}</TableCell>
                    <TableCell>
                      <span className="capitalize">{resource.type}</span>
                    </TableCell>
                    <TableCell>
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline truncate inline-block max-w-[200px]"
                      >
                        {resource.url}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive"
                        onClick={() => handleDeleteResource(resource.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 border rounded-md bg-muted/20">
              <p className="text-muted-foreground">No resources added yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonEditor;
